import { FC } from "react";
import { CartesianGrid, LabelList, Line, LineChart as RechartsLineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { UiColors } from "../../../config/ui/colors";

import { LineChartProps } from "./LineChart.type";
import { CustomizedYAxisTick } from "../shared/CustomizedYAxisTick/CustomizedYAxisTick";

const FormattedLabel = (props: any) => {
	const { x, y, value, width } = props;
	const formatter = new Intl.NumberFormat('nl-BE', {
		style: 'decimal',
		minimumFractionDigits: 0,
		maximumFractionDigits: 1,
	});
	if (value === null || value === 0 || value === undefined) return null;
	return (
		<text
			x={x + (width ?? 0) / 2}   // centers the label horizontally
			y={(y ?? 0) - 12}          // offset above the bar/point
			textAnchor="middle"
			fill={props.fill || '#000'}
			fontWeight="normal"
		>
			{formatter.format(value)}
		</text>
	);
};

const LineChart: FC<LineChartProps> = ({
	data = [],
	dataKeys = [],
	height,
	marginLeft = 0,
	XAxisLabel,
	XAxisProps,
	YAxisLabel,
	YAxisProps,
	labelProps,
	Tooltip: CustomTooltip,
	showToolTip = true,
	chartYAxisLabelPosition = "outside",
	customXTicks,
	xDomain,
	chartSubtitle
}) => {
	return (
		<div className="w-full">
			{YAxisLabel && <p className="text-s text-gray-800 font-bold">{YAxisLabel}</p>}
			{chartSubtitle && <p className="text-s text-gray-800">{chartSubtitle}</p>}

			<ResponsiveContainer width="99%" height={height}>
				<RechartsLineChart data={data} margin={{ left: marginLeft, top: 30, right: 0, bottom: 0 }} layout="horizontal">
					<CartesianGrid stroke={UiColors.charts.grid} />

					<XAxis
						{...XAxisProps}
						axisLine={{ stroke: UiColors.black, ...(typeof XAxisProps?.axisLine === "object" ? XAxisProps.axisLine : {}) }}
						tickMargin={10}
						// Set interval to 0 to prevent tick labels & grid lines to disappear
						interval={0}
						tick={{ fill: UiColors.black, ...(typeof XAxisProps?.tick === "object" ? XAxisProps.tick : {}) }}
						ticks={customXTicks}
						tickLine={false}
						type="number"
						domain={xDomain}
					/>

					<YAxis
						{...YAxisProps}
						axisLine={{ stroke: UiColors.black, strokeWidth: 0, ...(typeof YAxisProps?.axisLine === "object" ? YAxisProps.axisLine : {}) }}
						tickMargin={0}
						{...(chartYAxisLabelPosition === "outside" && {
							tick: (props) => <CustomizedYAxisTick {...props} {...(typeof YAxisProps?.tick === "object" && { ...YAxisProps.tick })} />,
						})}
						{...(chartYAxisLabelPosition === "inline" && {
							tick: false,
						})}
						tickLine={false} 
						width={1}
					/>

					{dataKeys.map((line) => (
						<Line
							key={`${line.name} - ${line.color}`}
							type="linear"
							dataKey={line.name}
							strokeWidth={2}
							stroke={line.color}
							dot={{ strokeWidth: 2, fill: line.color }}
							isAnimationActive={false}
						>
							{line?.showLabel && (
								<LabelList offset={12} fontWeight="bold" dataKey={line.name} content={<FormattedLabel />} position="top" fill={line.color} {...labelProps} />
							)}
						</Line>
					))}

					{typeof CustomTooltip !== "undefined" && (
						<Tooltip
							cursor={false}
							wrapperStyle={{ zIndex: 100 }}
							content={(props) => <CustomTooltip {...props} />}
							isAnimationActive={false}
						/>
					)}

					{typeof CustomTooltip === "undefined" && showToolTip ? <Tooltip /> : null}
				</RechartsLineChart>
			</ResponsiveContainer>

			{XAxisLabel && <p className="mt-3 text-xs text-black text-center">{XAxisLabel}</p>}
		</div>
	);
};

export { LineChart };
